import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { buildCategoricalScale, buildGroupedCategoricalScale, OCCUPANCY_COLORS, REGISTRATION_STATUS_COLORS } from "../lib/color";
import { seatFill, seatBorder, isNonSeatable, NO_REGION_OCCUPIED_FILL, type ColorMode } from "../lib/seatVisuals";

type Seat = Awaited<ReturnType<typeof api.sessionSeating>>["seats"][number];

const VIEW_WIDTH = 1600;
const VIEW_HEIGHT = 1000;

/**
 * Print/export view: the same hall plan, but the full extent always fits
 * on one page (no pan/zoom, no toolbar) so a headless browser can render it
 * straight to a PDF page. See src/backend/src/routes/export.ts — it
 * navigates here, waits for [data-export-ready], and calls page.pdf().
 */
export default function PrintHallPlanPage() {
  const { t, i18n } = useTranslation();
  const { eventSessionId } = useParams();
  const [searchParams] = useSearchParams();
  const colorMode = (searchParams.get("colorMode") as ColorMode) ?? "occupancy";
  const locale = searchParams.get("locale");
  const [seats, setSeats] = useState<Seat[] | null>(null);

  useEffect(() => {
    if (locale) i18n.changeLanguage(locale);
  }, [locale, i18n]);

  useEffect(() => {
    if (!eventSessionId) return;
    api.sessionSeating(Number(eventSessionId)).then((res) => setSeats(res.seats));
  }, [eventSessionId]);

  const scales = useMemo(() => {
    if (!seats) return { region: new Map(), bezirkBorder: new Map() };
    const regions = seats.map((s) => s.occupant?.region).filter((v): v is string => Boolean(v));
    const bezirke = seats.map((s) => s.occupant?.bezirk).filter((v): v is string => Boolean(v));
    const bezirkToRegion = new Map<string, string>();
    for (const seat of seats) {
      if (seat.occupant?.bezirk && seat.occupant?.region) bezirkToRegion.set(seat.occupant.bezirk, seat.occupant.region);
    }
    return { region: buildCategoricalScale(regions), bezirkBorder: buildGroupedCategoricalScale(bezirke, (b) => bezirkToRegion.get(b)) };
  }, [seats]);

  const legend = useMemo(() => {
    if (colorMode === "occupancy") {
      return [
        ["available", OCCUPANCY_COLORS.available],
        ["occupied", OCCUPANCY_COLORS.occupied],
        ["blocked", OCCUPANCY_COLORS.blocked],
        ["reserved", OCCUPANCY_COLORS.reserved],
      ] as const;
    }
    if (colorMode === "registrationStatus") {
      return [
        ["Zusage", REGISTRATION_STATUS_COLORS.Zusage],
        ["Absage", REGISTRATION_STATUS_COLORS.Absage],
        ["offen", REGISTRATION_STATUS_COLORS.offen],
      ] as const;
    }
    // In bezirk mode the legend shows the border-color meaning specifically
    // (the fill is the region color, already covered by the region legend).
    const scale = colorMode === "region" ? scales.region : scales.bezirkBorder;
    const entries = [...scale.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    // Only in "region" mode, since that's the fill this actually appears in
    // (bezirk mode's legend covers the border color, not the fill) — flag it
    // so a seated participant with no region on record doesn't read as an
    // empty seat on the printed plan.
    if (colorMode === "region" && seats?.some((s) => s.occupant && !s.occupant.region)) {
      entries.push([t("hallPlan.legend.noRegionData"), NO_REGION_OCCUPIED_FILL]);
    }
    return entries;
  }, [colorMode, scales, seats, t]);

  const ready = seats !== null;

  return (
    <div className="print-page" data-export-ready={ready ? "true" : "false"}>
      <svg viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`} width="100%" height="100%">
        {seats?.map((seat) => {
          const x = seat.positionX * VIEW_WIDTH;
          const y = seat.positionY * VIEW_HEIGHT;
          const border = seatBorder(seat, colorMode, scales);
          return (
            <g key={seat.id}>
              <rect
                x={x - 2.5}
                y={y - 2.5}
                width={5}
                height={5}
                rx={0.6}
                fill={seatFill(seat, colorMode, scales)}
                stroke={border ?? "rgba(0,0,0,0.2)"}
                strokeWidth={border ? 0.9 : 0.15}
              />
              {isNonSeatable(seat) && (
                <g className="print-seat-mark">
                  <line x1={x - 1.8} y1={y - 1.8} x2={x + 1.8} y2={y + 1.8} />
                  <line x1={x + 1.8} y1={y - 1.8} x2={x - 1.8} y2={y + 1.8} />
                </g>
              )}
            </g>
          );
        })}
      </svg>
      <div className="print-legend">
        {legend.map(([label, color]) => (
          <span key={label} className="print-legend-item">
            <span className="print-legend-swatch" style={{ background: color }} />
            {label}
          </span>
        ))}
      </div>
      <div className="print-timestamp">{new Date().toLocaleString(locale ?? "de")}</div>
    </div>
  );
}
